import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";

const cameraHelper = {};

/**
 * Gallery/content URIs often fail when read via fetch(webPath) in the WebView.
 * Base64 works for both camera and "From Photos".
 */
const getPhotoDefaults = {
  quality: 90,
  allowEditing: false,
  resultType: CameraResultType.Base64,
  source: CameraSource.Prompt,
  saveToGallery: false,
};

function mimeFromPhotoFormat(format) {
  const f = String(format || "jpeg").toLowerCase();
  if (f === "png") return "image/png";
  if (f === "webp") return "image/webp";
  return "image/jpeg";
}

cameraHelper.extensionForKycUpload = (photoOrFileData) => {
  if (!photoOrFileData?.format) return "jpg";
  const f = String(photoOrFileData.format).toLowerCase().replace(/^\./, "");
  if (f === "jpeg" || f === "jpg") return "jpg";
  if (f === "png" || f === "webp" || f === "gif") return f;
  return "jpg";
};

cameraHelper.getBlobFromCameraPhoto = async (photo) => {
  if (!photo) return null;
  if (photo.base64String) {
    try {
      const mime = mimeFromPhotoFormat(photo.format);
      const binary = atob(photo.base64String);
      const len = binary.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return new Blob([bytes], { type: mime });
    } catch (e) {
      console.error("cameraHelper.getBlobFromCameraPhoto base64 decode error", e);
      return null;
    }
  }
  if (photo.webPath) {
    try {
      const res = await fetch(photo.webPath);
      if (!res.ok) return null;
      return await res.blob();
    } catch (e) {
      console.error("cameraHelper.getBlobFromCameraPhoto fetch error", e);
      return null;
    }
  }
  return null;
};

cameraHelper.getPhoto = async ({
  labelHeader,
  labelCancel,
  labelPhoto,
  labelPicture,
  source,
} = {}) => {
  try {
    const opts = {
      ...getPhotoDefaults,
      ...(source !== undefined && source !== null ? { source } : {}),
      promptLabelHeader: labelHeader,
      promptLabelCancel: labelCancel,
      promptLabelPhoto: labelPhoto,
      promptLabelPicture: labelPicture,
    };
    const image = await Camera.getPhoto(opts);
    return image;
  } catch (error) {
    console.error("cameraHelper.getPhoto error", error);
    return undefined;
  }
};

/** CKYC manual “Take Photo” on native: opens device camera (Capacitor). */
cameraHelper.getKycNativeDocumentPhoto = () =>
  cameraHelper.getPhoto({ source: CameraSource.Camera });

/**
 * Multi-image gallery pick (uses Android Photo Picker where supported).
 * CKYC manual “From Gallery” uses a hidden `<input type="file">` so the WebView
 * opens the system picker without broad READ_MEDIA_* storage access.
 */
cameraHelper.pickImages = async ({ limit = 15 } = {}) => {
  try {
    const images = await Camera.pickImages({
      quality: 90,
      limit,
    });
    return images;
  } catch (error) {
    console.error("cameraHelper.pickImages error", error);
    return undefined;
  }
};

export { cameraHelper };
